import { useCallback, useEffect, useState } from "react";
import client from "../api/client";

/**
 * Converte erros do Zod para formato {campo: mensagem}
 * @param {Object} err - Erro lançado pelo Zod
 * @returns {Object} Dicionário de erros
 */
const formatZodErrors = (err) => {
	const zodErrors = {};
	if (err.errors) {
		err.errors.forEach((error) => {
			const field = error.path[0];
			zodErrors[field] = error.message;
		});
	}
	return zodErrors;
};

/**
 * Hook customizado para páginas CRUD padrão
 * Centraliza toda lógica de estado, fetch, validação e CUD operations
 */
export const useCrudPage = (endpoint, validationSchema, options = {}) => {
	const { defaultFormData = {}, onSuccess, transformBeforeSave } = options;

	// State management
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [page, setPage] = useState(1);
	const [limit] = useState(20);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [sort, setSort] = useState("id");
	const [order, setOrder] = useState("ASC");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [formData, setFormData] = useState(defaultFormData);
	const [errors, setErrors] = useState({});

	/**
	 * Busca dados paginados do backend
	 */
	const fetchData = useCallback(async () => {
		setLoading(true);
		try {
			const response = await client.get(endpoint, {
				params: { page, limit, search, sort, order },
			});
			setData(response.data.data || response.data);
			setTotal(response.data.total || 0);
		} catch (error) {
			console.error(`Error fetching ${endpoint}:`, error);
			setErrors({
				fetch: error.response?.data?.error || "Erro ao carregar dados",
			});
		} finally {
			setLoading(false);
		}
	}, [endpoint, page, limit, search, sort, order]);

	/**
	 * Effect para recarregar dados quando filtros mudam
	 */
	useEffect(() => {
		fetchData();
	}, [fetchData]);

	/**
	 * Abre modal para criar ou editar
	 */
	const handleOpenModal = useCallback(
		(item = null) => {
			if (item) {
				setEditingId(item.id);
				setFormData({ ...defaultFormData, ...item });
			} else {
				setEditingId(null);
				setFormData(defaultFormData);
			}
			setErrors({});
			setIsModalOpen(true);
		},
		[defaultFormData],
	);

	/**
	 * Fecha modal e limpa estado
	 */
	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		setEditingId(null);
		setFormData(defaultFormData);
		setErrors({});
	}, [defaultFormData]);

	/**
	 * Valida dados do formulário usando Zod schema
	 */
	const validateForm = useCallback(
		(currentFormData = formData) => {
			try {
				if (validationSchema) {
					validationSchema.parse(currentFormData);
				}
				setErrors({});
				return true;
			} catch (err) {
				setErrors(formatZodErrors(err));
				return false;
			}
		},
		[validationSchema, formData],
	);

	/**
	 * Submete formulário (create ou update)
	 */
	const handleSubmit = useCallback(
		async (e) => {
			if (e?.preventDefault) e.preventDefault();

			if (!validateForm()) {
				return Promise.resolve();
			}

			setSaving(true);
			setErrors({});

			try {
				const dataToSave = transformBeforeSave
					? transformBeforeSave(formData)
					: formData;

				if (editingId) {
					await client.put(`${endpoint}/${editingId}`, dataToSave);
				} else {
					await client.post(endpoint, dataToSave);
				}

				await fetchData();
				handleCloseModal();

				if (onSuccess) {
					onSuccess(editingId ? "updated" : "created");
				}
			} catch (error) {
				console.error("Error saving:", error);
				const errorMessage = error.response?.data?.error || "Erro ao salvar";
				setErrors({ submit: errorMessage });
				throw error;
			} finally {
				setSaving(false);
			}
		},
		[
			validateForm,
			formData,
			transformBeforeSave,
			editingId,
			endpoint,
			fetchData,
			handleCloseModal,
			onSuccess,
		],
	);

	/**
	 * Deleta um registro
	 */
	const handleDelete = useCallback(
		async (id, confirmMessage = "Tem certeza que deseja deletar?") => {
			if (!window.confirm(confirmMessage)) {
				return Promise.resolve();
			}

			try {
				await client.delete(`${endpoint}/${id}`);
				await fetchData();

				if (onSuccess) {
					onSuccess("deleted");
				}
			} catch (error) {
				console.error("Error deleting:", error);
				throw error;
			}
		},
		[endpoint, fetchData, onSuccess],
	);

	/**
	 * Atualiza um campo do formData
	 */
	const updateField = useCallback(
		(field, value) => {
			setFormData((prev) => ({ ...prev, [field]: value }));
			if (errors[field]) {
				setErrors((prev) => {
					const newErrors = { ...prev };
					delete newErrors[field];
					return newErrors;
				});
			}
		},
		[errors],
	);

	return {
		data,
		loading,
		saving,
		page,
		limit,
		total,
		search,
		sort,
		order,
		isModalOpen,
		editingId,
		formData,
		errors,
		setPage,
		setSearch,
		setSort,
		setOrder,
		setFormData,
		setErrors,
		updateField,
		handleOpenModal,
		handleCloseModal,
		handleSubmit,
		handleDelete,
		fetchData,
		validateForm,
		isEditing: !!editingId,
		hasErrors: Object.keys(errors).length > 0,
		totalPages: Math.ceil(total / limit),
	};
};
